import { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FaCircleArrowRight } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { useAppMode } from "src/contexts/app-mode";
import { useLocalStorage } from "usehooks-ts";

import {
  BOOKING_CONSTANTS,
  SHOW_HIDDEN_BOOKING_TYPES,
  TIME_CONSTANTS,
} from "@constants/Bookings";
import { STORAGE_KEYS } from "@constants/Kiosk";
import { LOCK_STATUS } from "@constants/unitDetails";
import { useLocationContext } from "@contexts/location/LocationContext";
import {
  EventBackgroundType,
  EventSubType,
  EventType,
} from "@enums/component.enum";
import { DateFormats } from "@enums/shared.enum";
import { EventContentArg } from "@fullcalendar/core";
import { useBooking } from "@hooks/useBooking/useBooking";
import { useLandingPageData } from "@hooks/useLandingPageData";
import {
  BookingCalendarApi,
  BookingCalendarEvent,
  BookingCalendarResources,
  BookingEventApi,
  BookingFetchResponseType,
  BookingSlotApi,
  ResourceType,
} from "@models/services/booking";
import { LandingPageSettings } from "@models/services/kiosk";
import { organisationResourceType } from "@models/services/organisation";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import bookingSlice from "@slices/booking.slice";
import bookingCalendarSlice from "@slices/bookingCalendar.slice";
import { RootState } from "@store/index";
import { policyLateBookingWindowMinutes } from "@utils/booking.utils";

import {
  getPreviousTimeSlots,
  useBackToBackBooking,
} from "../useBackToBackBooking/useBackToBackBooking";

export const useBookingCalendar = (slots: BookingFetchResponseType) => {
  const { isKioskMode } = useAppMode();
  const { activeLocation } = useLocationContext();

  const [deviceId] = useLocalStorage(STORAGE_KEYS.KIOSK_DEVICE_ID, "");
  const { data: landingPageData } = useLandingPageData(deviceId, {
    enabled: !!deviceId && isKioskMode,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { t } = useTranslation();

  const [resources, setResources] = useState<BookingCalendarResources[]>([]);
  const [calendarTime, setCalendarTime] = useState<{
    start: string;
    end: string;
  }>({
    end: TIME_CONSTANTS.END_OF_DAY,
    start: TIME_CONSTANTS.START_OF_DAY,
  });
  const {
    getNextStartTime,
    getOccupiedSlots,
    isAvailableTimeCalender,
    isLateBookingAndAvailableSlot,
    nextTimes,
    previousTimes,
  } = useBackToBackBooking();
  const { getNextTimeMark, getSelectedResource } = useBooking();
  const dispatch = useDispatch();

  const selectedDate = useSelector(
    (State: RootState) => State.booking.selectedDate
  );
  const backToBackBookingEnabled = useSelector(
    (State: RootState) => State.booking.backToBackBookingEnabled
  );

  const isAuthenticated = useSelector(
    (State: RootState) => State.local.isAuthenticated
  );

  const fetchCalendarData = async ({ locationId }: BookingSlotApi) => {
    if (!locationId && !slots?.showCalendar) {
      return;
    }

    setIsLoading(true);
    const data = await API_SERVICES.BOOKING.GET_BOOKING_EVENTS({
      bookingDate: selectedDate,
      isAuthenticated,
      locationId,
    })
      .then(response => {
        const responseData = handleResponse(response);
        if (responseData?.ok) {
          updateCalendarEvents(response.data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
    return data;
  };

  const fetchCalendarParticipant = async (bookingId: string) => {
    setIsLoading(true);
    const data = await API_SERVICES.BOOKING.GET_BOOKING_EVENTS_PARTICIPANT(
      bookingId
    )
      .then(response => {
        const responseData = handleResponse(response);
        if (responseData?.ok) {
          setIsLoading(false);
          return response.data;
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
    return data;
  };

  const updateCalendarEvents = (response: BookingCalendarApi) => {
    const responseEvents = response?.events ? response.events : [];
    setIsLoading(true);

    const hasQuotaExhausted = responseEvents.some(
      (event: BookingEventApi) => event.subType === EventSubType.QuotaExhausted
    );
    const events: BookingCalendarEvent[] = responseEvents
      .filter(
        event =>
          event !== null &&
          !(hasQuotaExhausted && event.subType === EventSubType.AdvanceBooking)
      )
      .map((event: BookingEventApi) => {
        let start = event.start;
        let end = event.end;

        if (moment(start, DateFormats.YMDTimeWithSeconds, true).isValid()) {
          start = moment(start, DateFormats.YMDTimeWithSeconds).format(
            DateFormats.YMDTime
          );
        }

        if (moment(end, DateFormats.YMDTimeWithSeconds, true).isValid()) {
          end = moment(end, DateFormats.YMDTimeWithSeconds).format(
            DateFormats.YMDTime
          );
        }

        if (event.type === EventType.Unavailable) {
          const resourceDetails = getSelectedResource(slots, event.resourceId);

          let startMoment = moment(start, DateFormats.YMDTime);

          if (!startMoment.isValid()) {
            startMoment = moment(
              resourceDetails?.slotInfo?.slotStart ||
                TIME_CONSTANTS.START_OF_DAY,
              DateFormats.YMDTime
            );
          }
          if (
            resourceDetails?.slotInfo?.slotStart &&
            moment(
              resourceDetails.slotInfo.slotStart || TIME_CONSTANTS.START_OF_DAY,
              DateFormats.YMDTime
            ).isAfter(startMoment)
          ) {
            startMoment = moment(
              resourceDetails.slotInfo.slotStart || TIME_CONSTANTS.START_OF_DAY,
              DateFormats.YMDTime
            );
          }

          startMoment = startMoment.add(1, DateFormats.Minutes);

          start = selectedDate + "T" + startMoment.format(DateFormats.Time);

          if (end === null && resourceDetails?.slotInfo?.slotEnd) {
            let endMoment = moment(
              resourceDetails.slotInfo.slotEnd,
              DateFormats.YMDTime
            );

            endMoment = endMoment.subtract(1, DateFormats.Minutes);

            end = selectedDate + "T" + endMoment.format(DateFormats.Time);
          }
        }
        return {
          ...event,
          bookingId: event?.id?.toString(),
          classNames:
            (isAuthenticated && !event.showPopup) ||
            SHOW_HIDDEN_BOOKING_TYPES.includes(event.type)
              ? ["disabled"]
              : undefined,
          display:
            event.type === EventType.Unavailable
              ? EventBackgroundType.Background
              : EventBackgroundType.Block,
          end,
          id:
            event.type === EventType.Exception
              ? event?.id?.toString() + "-" + event?.resourceId?.toString()
              : event?.id?.toString(),
          start,
          title: applyLocalization(event.title),
        };
      });

    let restrictionEvents: BookingCalendarEvent[] = [];
    const newCalendarTime =
      slots?.resources &&
      Array.isArray(slots.resources) &&
      slots.resources.length > 0
        ? calculateResourceData(slots.resources, events)
        : null;

    if (
      slots?.resources &&
      Array.isArray(slots.resources) &&
      slots.resources.length > 0 &&
      newCalendarTime
    ) {
      restrictionEvents = createResourceRestrictions(
        slots.resources,
        {
          end: newCalendarTime.end || TIME_CONSTANTS.END_OF_DAY,
          endTime: newCalendarTime.endTime,
          start: newCalendarTime.start || TIME_CONSTANTS.START_OF_DAY,
        },
        events
      );
    }

    const updatedEvents = events.map(event => {
      // Update the start/end time with the user's final opening/closing time
      if (
        event.isFullDay &&
        (event.type === EventType.Blocking ||
          event.type === EventType.Maintenance)
      ) {
        return {
          ...event,
          end:
            selectedDate +
            "T" +
            (newCalendarTime?.end ?? TIME_CONSTANTS.END_OF_DAY),
          start:
            selectedDate +
            "T" +
            (newCalendarTime?.start ?? TIME_CONSTANTS.START_OF_DAY),
        };
      }
      // Return the event unchanged if it doesn't match the criteria
      return event;
    });
    dispatch(
      bookingCalendarSlice.actions.setCalendarEvents([
        ...updatedEvents,
        ...restrictionEvents,
      ])
    );
    setIsLoading(false);
  };

  const createResourceRestrictions = (
    resources: ResourceType[],
    calendarTime: { start: string; end: string; endTime: moment.Moment },
    existingEvents: BookingCalendarEvent[]
  ): BookingCalendarEvent[] => {
    if (!resources || !Array.isArray(resources)) return [];

    const slotTime = slots?.slotSplit ?? BOOKING_CONSTANTS.WIDTH_PER_SLOT;
    const currentDate = moment(selectedDate).format(DateFormats.YMD);

    const restrictionEvents: BookingCalendarEvent[] = [];
    const backToBackBookingSlotsList: {
      resourceId: string;
      validAvailableSlots: string[];
    }[] = [];

    const addRestriction = (event: BookingCalendarEvent) => {
      const eventStart = moment(event.start);
      const eventEnd = moment(event.end);

      const overlapsExisting = existingEvents.some(
        e =>
          e.resourceId === event.resourceId &&
          e.type === EventType.Unavailable &&
          eventStart.isBefore(e.end) &&
          eventEnd.isAfter(e.start)
      );

      if (overlapsExisting) {
        return;
      }

      restrictionEvents.push(event);
    };

    const isResourceBookingEnabledData =
      isKioskMode && landingPageData
        ? getIsResourceBookingEnabledData(landingPageData)
        : {};

    const activLocationResourceObj: Record<string, organisationResourceType> =
      Object.fromEntries((activeLocation?.resources ?? []).map(r => [r.id, r]));

    resources.forEach(resource => {
      if (!resource?.slotInfo) return;

      const { slotEnd, slots: slotConfig, slotStart } = resource.slotInfo;
      const availableSlots = slotConfig?.availableSlots || [];

      const startOfDay = moment(
        `${currentDate}T${calendarTime.start || TIME_CONSTANTS.START_OF_DAY}`,
        DateFormats.YMDTime
      );

      if (
        slotStart &&
        startOfDay.isBefore(moment(slotStart, DateFormats.YMDTime))
      ) {
        addRestriction({
          classNames: ["fg-event-disabled"],
          display: EventBackgroundType.Background,
          end: slotStart,
          id: `fg-disabled-${resource.id}-before`,
          resourceId: resource.id,
          start: `${currentDate}T${startOfDay.format(DateFormats.Time)}`,
          title: t("Calendar.notAvailable"),
          type: EventType.Restriction,
        });
      }

      const slotEndMoment = moment(slotEnd);

      if (slotEnd && calendarTime.endTime?.isAfter(slotEndMoment)) {
        addRestriction({
          classNames: ["fg-event-disabled"],
          display: EventBackgroundType.Background,
          end: `${currentDate}T${calendarTime.endTime?.format(DateFormats.Time) || TIME_CONSTANTS.END_OF_DAY}`,
          id: `fg-disabled-${resource.id}-after`,
          resourceId: resource.id,
          start: slotEnd,
          title: t("Calendar.notAvailable"),
          type: EventType.Restriction,
        });
      }

      const allowedDurations = slotConfig?.allowedDurations || {};

      let validAvailableSlots =
        isKioskMode && !isAuthenticated
          ? availableSlots
          : availableSlots.filter(slot => !!allowedDurations[`T_${slot}`]);

      if (
        backToBackBookingEnabled &&
        (!isKioskMode || (isKioskMode && isAuthenticated))
      ) {
        const isAvilableslot: string[] = [];
        validAvailableSlots = validAvailableSlots.filter(slot => {
          const getPreviousTimeslot = getPreviousTimeSlots(
            allowedDurations,
            slot
          );
          const isAvailable = getPreviousTimeslot.some(time =>
            isAvilableslot.includes(time)
          );
          const isToday = selectedDate === moment().format(DateFormats.YMD);
          if (isAvailable) {
            const occupiedSlots = getOccupiedSlots(resource);
            const hasPreviousOccupiedSlot = previousTimes(
              slot,
              resource?.policyInfo?.bookingStartTimes ?? ["15"]
            ).some(previousTime => occupiedSlots.includes(previousTime));
            const isCurrentTime =
              (isToday &&
                getNextStartTime(
                  resource?.policyInfo?.bookingStartTimes ?? ["15"],
                  0
                ) === slot) ||
              isLateBookingAndAvailableSlot(resource, slot);
            const nextSlotTimes = nextTimes(slot);

            const hasOccupiedNextSlot = nextSlotTimes.some(nextTime =>
              occupiedSlots.includes(nextTime)
            );

            if (
              hasOccupiedNextSlot &&
              !hasPreviousOccupiedSlot &&
              !isCurrentTime
            ) {
              return true;
            }
          }

          const isAvailableBookTime = isAvailableTimeCalender(
            resource,
            slot,
            isAvilableslot
          );

          if (isAvailableBookTime) {
            isAvilableslot.push(slot);
          } else if (isLateBookingAndAvailableSlot(resource, slot)) {
            const slotStartMoment = moment(
              `${currentDate}T${slot}`,
              DateFormats.YMDTime
            );
            const slotEndMoment = slotStartMoment
              .clone()
              .add(slotTime, "minutes");
            addRestriction({
              classNames: ["fg-event-disabled"],
              display: EventBackgroundType.Background,
              end: slotEndMoment.format(DateFormats.YMDTime),
              id: `fg-gap-${resource.id}-${slot.replace(":", "")}`,
              resourceId: resource.id,
              start: slotStartMoment.format(DateFormats.YMDTime),
              title: t("Calendar.notAvailable"),
              type: EventType.Restriction,
            });
            return false;
          }
          const isAllowed = isAvailableBookTime || isAvailable;
          if (!isAllowed) {
            const slotStartMoment = moment(
              `${currentDate}T${slot}`,
              DateFormats.YMDTime
            );
            const slotEndMoment = slotStartMoment
              .clone()
              .add(slotTime, "minutes");
            addRestriction({
              classNames: ["fg-event-disabled"],
              display: EventBackgroundType.Background,
              end: slotEndMoment.format(DateFormats.YMDTime),
              id: `fg-gap-${resource.id}-${slot.replace(":", "")}`,
              resourceId: resource.id,
              start: slotStartMoment.format(DateFormats.YMDTime),
              title: t("Calendar.notAvailable"),
              type: EventType.Restriction,
            });
          }

          return isAllowed;
        });
        backToBackBookingSlotsList.push({
          resourceId: resource.id,
          validAvailableSlots: isAvilableslot,
        });
      }
      const sortedSlots = [...validAvailableSlots].sort((a, b) =>
        moment(`${currentDate}T${a}`, DateFormats.YMDTime).diff(
          moment(`${currentDate}T${b}`, DateFormats.YMDTime)
        )
      );

      const available = sortedSlots.map(slot =>
        moment(`${currentDate}T${slot}`, DateFormats.YMDTime)
      );

      let lastEnd = startOfDay.clone();

      if (moment(selectedDate, DateFormats.YMD).isBefore(moment(), "day")) {
        addRestriction({
          classNames: ["fg-event-disabled"],
          display: EventBackgroundType.Background,
          end:
            calendarTime.endTime?.format(DateFormats.YMDTime) ||
            TIME_CONSTANTS.END_OF_DAY,
          id: `fg-unavailable-past-${resource.id}`,
          resourceId: resource.id,
          start: lastEnd.format(DateFormats.YMDTime),
          title: t("Calendar.notAvailable"),
          type: EventType.Unavailable,
        });

        lastEnd = calendarTime.endTime.clone();
      } else if (selectedDate === moment().format(DateFormats.YMD)) {
        const isToday = selectedDate === moment().format(DateFormats.YMD);
        const lateBookingWindow = isToday
          ? policyLateBookingWindowMinutes(resource?.policyInfo)
          : 0;
        const referenceTime = moment().subtract(lateBookingWindow, "minutes");
        const now = getNextTimeMark(referenceTime, slotTime, referenceTime);

        if (now.isAfter(lastEnd)) {
          addRestriction({
            classNames: ["fg-event-disabled"],
            display: EventBackgroundType.Background,
            end: now.format(DateFormats.YMDTime),
            id: `fg-unavailable-today-${resource.id}`,
            resourceId: resource.id,
            start: lastEnd.format(DateFormats.YMDTime),
            title: t("Calendar.notAvailable"),
            type: EventType.Unavailable,
          });

          lastEnd = now.clone();
        }
      }

      if (backToBackBookingEnabled && available.length > 0) {
        const lastSlot = available[available.length - 1];
        const nextTime = lastSlot.clone().add(slotTime, "minutes");
        const isBookingEndAtSameDay =
          resource?.policyInfo?.bookingEndAt &&
          moment(resource.policyInfo.bookingEndAt).isValid() &&
          moment(resource.policyInfo.bookingEndAt).isSame(
            moment(selectedDate),
            "day"
          );
        const restrictionEnd =
          isBookingEndAtSameDay && resource?.policyInfo?.bookingEndAt
            ? resource.policyInfo.bookingEndAt
            : slotEnd ||
              `${currentDate}T${calendarTime.endTime?.format(DateFormats.Time) || calendarTime.end || TIME_CONSTANTS.END_OF_DAY}`;

        if (isBookingEndAtSameDay && moment(restrictionEnd).isAfter(nextTime)) {
          addRestriction({
            classNames: ["fg-event-disabled"],
            display: EventBackgroundType.Background,
            end: restrictionEnd,
            id: `fg-gap-${resource.id}-backtoback-last`,
            resourceId: resource.id,
            start: nextTime.format(DateFormats.YMDTime),
            title: t("Calendar.notAvailable"),
            type: EventType.Restriction,
          });
          lastEnd = moment(restrictionEnd);
        }
      }

      available.forEach(slotMoment => {
        if (slotMoment.isAfter(lastEnd)) {
          addRestriction({
            classNames: ["fg-event-disabled"],
            display: EventBackgroundType.Background,
            end: slotMoment.format(DateFormats.YMDTime),
            id: `fg-gap-${resource.id}-${lastEnd.format("HHmm")}`,
            resourceId: resource.id,
            start: lastEnd.format(DateFormats.YMDTime),
            title: t("Calendar.notAvailable"),
            type: EventType.Restriction,
          });
        }

        lastEnd = slotMoment.clone().add(slotTime, "minutes");
      });

      if (
        (isKioskMode &&
          !isResourceBookingEnabledData[resource.id]?.isBookingAllowed) ||
        (!isKioskMode &&
          activLocationResourceObj[resource.id]?.lockStatus ===
            LOCK_STATUS.CLOSE)
      ) {
        addRestriction({
          classNames: ["fg-event-disabled"],
          display: EventBackgroundType.Background,
          end: slotEnd,
          id: `fg-disabled-${resource.id}-kiosk-unavailable`,
          resourceId: resource.id,
          start: slotStart,
          title: t("Calendar.notAvailable"),
          type: EventType.Restriction,
        });
      }

      if (lastEnd.isBefore(calendarTime.endTime)) {
        addRestriction({
          classNames: ["fg-event-disabled"],
          display: EventBackgroundType.Background,
          end:
            calendarTime.endTime?.format(DateFormats.YMDTime) ||
            TIME_CONSTANTS.END_OF_DAY,
          id: `fg-gap-${resource.id}-end`,
          resourceId: resource.id,
          start: lastEnd.format(DateFormats.YMDTime),
          title: t("Calendar.notAvailable"),
          type: EventType.Restriction,
        });
      }
    });

    if (backToBackBookingEnabled) {
      dispatch(
        bookingSlice.actions.setBackToBackBookingSlots(
          backToBackBookingSlotsList
        )
      );
    } else {
      dispatch(bookingSlice.actions.setBackToBackBookingSlots([]));
    }

    return mergeRestrictions(restrictionEvents);
  };

  const mergeRestrictions = (events: BookingCalendarEvent[]) => {
    const grouped = events.reduce<Record<string, BookingCalendarEvent[]>>(
      (acc, ev) => {
        acc[ev.resourceId] = acc[ev.resourceId] || [];
        acc[ev.resourceId].push(ev);
        return acc;
      },
      {}
    );

    const merged: BookingCalendarEvent[] = [];

    Object.values(grouped).forEach(group => {
      const sorted = group.sort(
        (a, b) => moment(a.start).valueOf() - moment(b.start).valueOf()
      );

      let current = sorted[0];

      for (let i = 1; i < sorted.length; i++) {
        const ev = sorted[i];

        if (moment(ev.start).isSameOrBefore(current.end)) {
          if (moment(ev.end).isAfter(current.end)) {
            current.end = ev.end;
          }
        } else {
          merged.push(current);
          current = ev;
        }
      }

      merged.push(current);
    });

    return merged;
  };

  const normalizeEvents = (events: BookingCalendarEvent[]) => {
    if (!events || !Array.isArray(events)) return [];

    return events.map(e => {
      if (e?.end?.includes(TIME_CONSTANTS.INVALID_END)) {
        const datePart = e.end.split("T")[0];
        const nextDay = new Date(datePart);
        nextDay.setDate(nextDay.getDate() + 1);
        const fixedEnd =
          nextDay.toISOString().split("T")[0] +
          "T" +
          TIME_CONSTANTS.START_OF_DAY;
        return { ...e, end: fixedEnd };
      }
      return e;
    });
  };

  const calculateResourceData = (
    resourcesData: ResourceType[],
    events: BookingCalendarEvent[]
  ): {
    end: string;
    start: string;
    endTime: moment.Moment;
  } => {
    if (
      (!resourcesData || resourcesData.length === 0) &&
      (!events || events.length === 0)
    ) {
      setCalendarTime({
        end: TIME_CONSTANTS.END_OF_DAY,
        start: TIME_CONSTANTS.START_OF_DAY,
      });
      setResources([]);
      return {
        end: TIME_CONSTANTS.END_OF_DAY,
        endTime: moment(TIME_CONSTANTS.END_OF_DAY, DateFormats.Time),
        start: TIME_CONSTANTS.START_OF_DAY,
      };
    }

    const starts = resourcesData
      .map(r => r?.slotInfo?.slotStart)
      .filter(Boolean)
      .map(t => moment(t, DateFormats.YMDTime));

    const ends = resourcesData
      .map(r => r?.slotInfo?.slotEnd)
      .filter(Boolean)
      .map(t => moment(t, DateFormats.YMDTime));

    const eventStart = events
      .filter(
        r =>
          r?.start !== null &&
          !(
            r?.isFullDay &&
            (r?.type === EventType.Blocking ||
              r?.type === EventType.Maintenance)
          )
      )
      .map(event => moment(event?.start, DateFormats.YMDTime));

    const eventEnd = events
      .filter(
        e =>
          e?.end !== null &&
          !(
            e?.isFullDay &&
            (e?.type === EventType.Blocking ||
              e?.type === EventType.Maintenance)
          )
      )
      .map(event => moment(event?.end, DateFormats.YMDTime));

    if (starts.length === 0) {
      const StartDayMoment = moment(
        `${selectedDate}T${TIME_CONSTANTS.START_OF_DAY}`,
        DateFormats.YMDTime
      );

      starts.push(StartDayMoment);
    }

    if (ends.length === 0) {
      const EndDayMoment = moment(
        `${selectedDate}T${TIME_CONSTANTS.END_OF_DAY}`,
        DateFormats.YMDTime
      );
      ends.push(EndDayMoment);
    }

    starts.push(...eventStart);
    ends.push(...eventEnd);

    const startTime = moment.min(starts);
    const endTime = moment.max(ends);

    const updateEndTime = endTime.clone();

    const normalizedStart = startTime?.isValid()
      ? startTime.format(DateFormats.Time)
      : TIME_CONSTANTS.START_OF_DAY;

    const normalizedEnd =
      updateEndTime &&
      updateEndTime.isValid() &&
      updateEndTime.format(DateFormats.Time) !== TIME_CONSTANTS.INVALID_END &&
      updateEndTime.format(DateFormats.Time) !== TIME_CONSTANTS.START_OF_DAY
        ? updateEndTime.format(DateFormats.Time)
        : TIME_CONSTANTS.END_OF_DAY;

    const newCalendarTime = {
      end: normalizedEnd,
      endTime,
      start: normalizedStart,
    };

    setCalendarTime(newCalendarTime);

    const resources = resourcesData
      .filter(r => r?.slotInfo)
      .map((r, index) => {
        return {
          businessHours: [],
          id: r.id,
          slotEnd: normalizedEnd,
          slotStart: normalizedStart,
          sortOrder: index,
          title: applyLocalization(r.title),
        };
      });
    setResources(resources);

    return newCalendarTime;
  };

  const getIsResourceBookingEnabledData = (
    landingPageData: LandingPageSettings
  ) =>
    (landingPageData.resources ?? []).reduce<
      Record<string, { isBookingAllowed: boolean }>
    >((acc, { isBookingAllowed, resourceId }) => {
      acc[resourceId] = { isBookingAllowed };
      return acc;
    }, {});

  const renderEventContent = (eventInfo: EventContentArg) => {
    const { event } = eventInfo;

    const eventType = event.extendedProps?.subType ?? event.extendedProps?.type;

    const hasType = !!eventType;
    const hasTitle = !!event.title;

    const shouldShowTimeOnly =
      (!hasType && !hasTitle) || eventType === EventType.TempBooking;

    let displayText = "";

    if (shouldShowTimeOnly && event.start && event.end) {
      const startMoment = moment(event.start);
      const endMoment = moment(event.end);

      const startTime = startMoment.format(DateFormats.Time);
      const endTime = endMoment.format(DateFormats.Time);

      displayText = `${startTime} - ${endTime}`;
    }

    if (shouldShowTimeOnly) {
      return <div className="ml-1">{displayText}</div>;
    }
    const isBooking = eventType === EventType.Booking;

    const subType = event.extendedProps?.subType;
    const afterAuthClickable = isAuthenticated
      ? !!event.extendedProps?.showDrawer || !!event.extendedProps?.showPopup
      : true;
    const isClickable =
      !(subType && SHOW_HIDDEN_BOOKING_TYPES.includes(subType)) &&
      afterAuthClickable;

    const participants =
      isBooking && Array.isArray(event.extendedProps?.participants)
        ? event.extendedProps.participants
        : isBooking && event.extendedProps?.participants
          ? [event.extendedProps.participants]
          : [];

    const getDisplayName = (
      item:
        | { id?: string | number; userName?: string }
        | string
        | null
        | undefined
    ): string => {
      if (!item) return "";
      if (typeof item === "string") return item;
      return item.userName || "";
    };

    const guestName = participants[0] ? getDisplayName(participants[0]) : "";
    const additionalCount =
      participants.length > 1 ? participants.length - 1 : 0;

    const notRestrictedEvent =
      eventType !== EventType.Restriction &&
      eventType !== EventType.Unavailable;

    return (
      <div
        className={
          notRestrictedEvent
            ? "d-flex justify-content-between overflow-hidden align-items-stretch h-100 px-1 pb-1"
            : "mt-1 ml-1"
        }
      >
        <div className={notRestrictedEvent ? "ml-1" : ""}>
          {event?.title ? (
            <>
              <div className="ml-1 mb-1 text-truncate" title={event.title}>
                {event.title}
              </div>
              {isBooking && guestName && (
                <small
                  className="ml-1 text-truncate fw-normal d-flex gap-1 align-items-center"
                  title={guestName}
                >
                  {guestName}
                  {additionalCount > 0 && (
                    <span className="fg-booked-additional-count badge text-bg-light text-white bg-opacity-25 ml-1">
                      +{additionalCount}
                    </span>
                  )}
                </small>
              )}
            </>
          ) : (
            isBooking && (
              <>
                <div className="mb-1 ml-1 text-truncate" title={t("Booking")}>
                  {t("Booking")}
                </div>
              </>
            )
          )}
        </div>
        {isBooking && isClickable && (
          <div className="align-content-end">
            <FaCircleArrowRight />
          </div>
        )}
      </div>
    );
  };

  return {
    calendarTime,
    createResourceRestrictions,
    fetchCalendarData,
    fetchCalendarParticipant,
    isLoading,
    normalizeEvents,
    renderEventContent,
    resources,
    setIsLoading,
    updateCalendarEvents,
  };
};
